import useFeatureTags from "@custom-hooks/useFeatureTags";
import { getInputNodesForNodeLabel } from "@views/thinking-layout-editor/util";
import { forEach, get, isNil, lowerCase, map, upperFirst } from "lodash";
import { useReactFlow } from "reactflow";
import { useHomeChildContainer } from "./childContainer";
import { useHomeUsePluginContainer } from "./container";
import func from "@uikit/func";
import isBlank from "@sedan-utils/is-blank";

type Platform = "saas" | "saasV2" | "thinkingApp";

export function useNodesTitle(id: string, isInBlueprint = false) {
  const {
    isNewEdgeRunRef,
    saasAndThinkState: {
      saasUIData,
      saasV2UIData,
      thinkingAppUIData,
      setSaasUIData,
      setSaasV2UIData,
      setThinkingAppUIData,
    },
    inputTitleState,
  } = useHomeUsePluginContainer();

  const { getAndSetQueryV3 } = useHomeChildContainer();
  const { setNodes, getNode, getEdges, getNodes } = useReactFlow();
  const featureTags = useFeatureTags();

  function getSaasAction(pf: Platform) {
    const saasDataMap = {
      saas: {
        type: "saas",
        resetData: setSaasUIData,
        data: saasUIData,
      },
      saasV2: {
        type: "saasV2",
        resetData: setSaasV2UIData,
        data: saasV2UIData,
      },
      thinkingApp: {
        type: "thinkingApp",
        resetData: setThinkingAppUIData,
        data: thinkingAppUIData,
      },
    };
    return saasDataMap[pf];
  }

  const saveTitle = async (title = inputTitleState.value.title) => {
    await setNodes(nodes => {
      return map(nodes, node => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              inputTitle: title,
            },
          };
        }
        return node;
      });
    });

    await forEach(["saas", "saasV2", "thinkingApp"], async (platform: Platform) => {
      const { data, resetData } = await getSaasAction(platform);

      resetData((prevData: any) => {
        return {
          ...prevData,
          input: data.input.map((x: any) => {
            if (x.id === id) {
              return {
                ...x,
                data: {
                  ...x.data,
                  inputTitle: title,
                },
              };
            }
            return x;
          }),
        };
      });
    });
  };

  const handleEnter = () => {
    handleBlur();
  };

  const handleBlur = async () => {
    const node = getNode(id);
    inputTitleState.value = { id, title: inputTitleState.value.title?.trim() };

    const oldTitle = node?.data.inputTitle;
    await saveTitle();

    // v3 当值有变化的时候才更新prompt
    if (isNewEdgeRunRef.current && oldTitle !== inputTitleState.value.title) {
      await refreshPrompt(oldTitle);
    }
    inputTitleState.value = { id: "", title: "" };
  };

  const refreshPrompt = async (oldTitle: string) => {
    const edges = getEdges();
    const nodes = getNodes();
    const edgeIds = edges.filter(x => x.source === inputTitleState.value.id).map(x => x.target);
    const results: Array<{ edgeId: string; enterText: string }> = [];
    let repeat = false;
    for (let edgeId of edgeIds) {
      const edgeNode = getNode(edgeId);
      if (edgeNode && edgeNode?.data.flows.length > 1) {
        const sourceNodeIds = edgeNode?.data.flows.map((x: any) => x.sourceNodeId);
        const sourceTitles = nodes
          .filter(x => sourceNodeIds.includes(x.id))
          .map(x => x.data.inputTitle || x.data.generatedLabel || "Input");
        // 重新生成prompt
        if (new Set(sourceTitles).size !== sourceNodeIds.length) {
          repeat = true;
        }
        results.push({ edgeId, enterText: edgeNode?.data.lineParam.enterText });
      }
    }

    if (repeat) {
      // 还原title
      func.customMsg({
        content: "A node with the same name already exists",
        type: "warning",
      });
      await saveTitle(oldTitle);
    } else {
      // 更新prompt
      await results.forEach(async ({ edgeId, enterText }) => {
        await getAndSetQueryV3(edgeId, enterText);
      });
    }
  };

  const handleFocus = () => {
    const node = getNode(id);
    inputTitleState.value = { id, title: get(node, "data.inputTitle") };
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = e => {
    inputTitleState.value = { id, title: e.target.value };
  };

  const showNodeLabel = () => {
    const edges = getEdges();
    const nodes = getNodes();
    const node = getNode(id);
    const inputeNodes = getInputNodesForNodeLabel(nodes, edges);
    // in COT sometimes node ids are just number without the `editor` prefix, need to find out why
    const isInputNode = inputeNodes.includes(id) || inputeNodes.includes(`editor-${id}`);
    const isBluePrintNode = get(node, "data.displayType") === "blueprint";
    const showBluePrintLabel = (isBluePrintNode || isInBlueprint) && !isBlank(node?.data?.textAreaValue?.name);
    return featureTags.showNodeLabel && (isInputNode || showBluePrintLabel);
  };

  const node = getNode(id);
  const isBluePrintNode = get(node, "data.displayType") === "blueprint";
  // BluePrintNode的label取textAreaValue.name
  const nodeTitle = isBluePrintNode || isInBlueprint ? node?.data?.textAreaValue?.name : node?.data?.inputTitle;
  const nodeLabel = upperFirst(lowerCase(node?.data?.displayType));
  const title = isNil(nodeTitle) ? node?.data?.generatedLabel || nodeLabel : nodeTitle;

  return {
    inputTitle: inputTitleState.value.id !== id ? title : inputTitleState.value.title,
    disableInput: isBluePrintNode || isInBlueprint,
    handleEnter,
    handleBlur,
    handleFocus,
    handleChange,
    saveTitle,
    showNodeLabel,
  };
}
